// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { getCurrentDate } from '@shared/ui-kit/utils/get-current-date';
import { type BasePortfolio } from '@shared/data-access/interfaces/common/base-portfolio';
import { useState } from 'react';
import { setWorkflow } from '@advisor-app/store/slices/workflow';
import * as XLSX from 'xlsx';
import { useHandleApiErrors } from '@advisor-app/hooks';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { useAppDispatch } from '@advisor-app/store';

export const useGetMutualFunds = () => {
  const handleApiErrors = useHandleApiErrors();
  const runSafeUpdate = useSafeUpdate();
  const dispatch = useAppDispatch();

  const [openModalCash, setOpenModalCash] = useState(false);
  const [transformedDataMF, setTransformedDataMF] = useState<BasePortfolio>({
    ticker: [],
    weight: [],
    unrealized_capital_gains: [],
    date: [],
  });

  const [getFundHoldings] = assetsApi.useLazyFundHoldingsQuery();

  const handleOnExport = async (tickerMutualFund: string) => {
    try {
      const tickersMF = await getFundHoldings({
        params: { ticker: tickerMutualFund, assets_page: 99999, page: 0 },
        assetType: 'mutual_fund',
      }).unwrap();

      const tickers = tickersMF.data.filter(asset => asset.ticker !== '');

      const wordBook = XLSX.utils.book_new();
      const wordSheet = XLSX.utils.json_to_sheet(tickers);

      XLSX.utils.book_append_sheet(wordBook, wordSheet);
      XLSX.writeFile(wordBook, `mutual_fund_${tickerMutualFund}.xlsx`);
    } catch (error: any) {
      handleApiErrors(error);
    }
  };

  const loadMutualFundOnRedux = async (tickerMutualFund: string) => {
    // We receive the row from the table and open pupup
    try {
      runSafeUpdate(() => setOpenModalCash(true));

      const transformedData: BasePortfolio = {
        ticker: [],
        weight: [],
        unrealized_capital_gains: [],
        date: [],
      };

      const currentDate = getCurrentDate();

      runSafeUpdate(() => dispatch(setWorkflow({ type: 'load', asset: 'stock' })));

      const tickersMF = await getFundHoldings({
        params: { ticker: tickerMutualFund, assets_page: 99999, page: 0 },
        assetType: 'mutual_fund',
      }).unwrap();

      const filteredArray = tickersMF.data.filter((obj, index, arr) => {
        return index === arr.findIndex(t => t.ticker === obj.ticker);
      });

      const tickersMFWithoutEmpty = filteredArray.filter(
        ticker => ticker.ticker !== '',
      );
      // sum of all percentages
      const sumPercentageWithoutEmpty = tickersMFWithoutEmpty.reduce(
        (acc, ticker) => acc + ticker.percentage,
        0,
      );
      // change percentage to new 100%
      const tickersMFWithoutEmptyPercentage = tickersMFWithoutEmpty.map(ticker => {
        return {
          ...ticker,
          percentage: (ticker.percentage * 100) / sumPercentageWithoutEmpty,
        };
      });

      tickersMFWithoutEmptyPercentage.forEach(item => {
        transformedData.ticker.push(item.ticker);
        transformedData.weight.push(item.percentage / 100);
        transformedData.unrealized_capital_gains.push(0);
        transformedData.date.push(currentDate);
      });
      runSafeUpdate(() => {
        setTransformedDataMF(transformedData);
      });
    } catch (error: any) {
      handleApiErrors(error);
    }
  };

  const handleCloseModal = () => setOpenModalCash(false);

  return {
    transformedDataMF,
    handleCloseModal,
    handleOnExport,
    loadMutualFundOnRedux,
    openModalCash,
  };
};
